// Binder API service — ported from public/api-v5.js
// V5 unified HOT/WARM/COLD path architecture for all binder types

import { extractSourceInfo, toShortPath } from '../logic/url-parser.js';
import {
  getCacheKey,
  getCachedCollectionInitial,
  cacheCollectionInitial,
  isCacheAvailable,
} from './indexeddb-cache.js';
import { API_ENDPOINTS } from './endpoints.js';
import { apiFetch } from './api-fetch.js';

// Page size for V5 API calls — multiple of 9 (28 binder pages per fetch)
const V5_PAGE_SIZE = 252;

/**
 * Strips unnecessary properties from Scryfall card data to reduce memory usage.
 * Keeps only properties required for rendering and functionality.
 */
export function stripUnnecessaryCardData(cardData) {
  if (cardData.not_found) {
    return cardData;
  }

  const stripped = {
    id: cardData.id,
    oracle_id: cardData.oracle_id,
    name: cardData.name,
    set: cardData.set,
    set_name: cardData.set_name,
    collector_number: cardData.collector_number,
    layout: cardData.layout,
    type_line: cardData.type_line,
    color_identity: cardData.color_identity,
    colors: cardData.colors,
    released_at: cardData.released_at,
    rarity: cardData.rarity,
    frame: cardData.frame,
    image_uris: cardData.image_uris,
    card_faces: cardData.card_faces
      ? cardData.card_faces.map((face) => ({
          name: face.name,
          image_uris: face.image_uris,
        }))
      : undefined,
    prices: cardData.prices,
    purchase_uris: cardData.purchase_uris,
    finishes: cardData.finishes,
    lang: cardData.lang,
    condition: cardData.condition,
    quantity: cardData.quantity,
    is_foil: cardData.is_foil,
    board: cardData.board,
    finish: cardData.finish,
    finish_type: cardData.finish_type,
    price_usd: cardData.price_usd,
    price_eur: cardData.price_eur,
    printing_specified: cardData.printing_specified,
    set_symbol_url: cardData.set_symbol_url,
  };

  // Remove undefined properties
  Object.keys(stripped).forEach((key) => {
    if (stripped[key] === undefined) {
      delete stripped[key];
    }
  });

  return stripped;
}

/**
 * Loads a collection using the V5 unified endpoint.
 * Uses HOT/WARM/COLD path architecture:
 * - HOT: Binder synced and fresh → SQL JOIN (~50ms)
 * - WARM: Binder synced but stale → SQL JOIN + queue refresh
 * - COLD: Binder not synced → queue sync, poll until ready
 */
async function loadCollectionV5(sourceInfo, options = {}) {
  const endpoint = API_ENDPOINTS.get_binder_page_v5;
  if (!endpoint) {
    throw new Error('V5 endpoint not configured');
  }

  const { source, type: sourceType, id: sourceId } = sourceInfo;
  const currency = options.currency || 'usd';

  console.log(`V5: Loading collection ${source}/${sourceType}/${sourceId}`);

  // A public resource: the page reads no Viewer, so the request carries no
  // token (nothing for the edge to verify) and never waits on auth. The
  // star's want state is a separate read (stores/want-hits.svelte.js).
  /** @param {'price' | 'color'} sortMode */
  const fetchFirstPage = (sortMode) =>
    apiFetch(
      endpoint,
      {
        method: 'POST',
        json: { source, sourceType, sourceId, page: 1, pageSize: V5_PAGE_SIZE, sortMode, currency },
      },
      { token: null }
    ).then((r) => r.json());

  // Fetch price sort first, polling on COLD path
  let priceResult;
  const maxPollAttempts = 120;
  const pollIntervalMs = 2000;

  for (let attempt = 0; attempt <= maxPollAttempts; attempt++) {
    priceResult = await fetchFirstPage('price');

    if (!priceResult.success) {
      const errorMsg = priceResult.sync?.error || priceResult.error || 'V5 price fetch failed';
      throw new Error(errorMsg);
    }

    const sync = priceResult.sync || {};

    if (sync.status === 'error') {
      throw new Error(sync.error || 'Sync failed');
    }

    if (sync.status !== 'syncing' || sync.pageReady) {
      break;
    }

    if (attempt === maxPollAttempts) {
      throw new Error('Sync timeout: collection data not available after 2 minutes');
    }

    console.log(`V5: COLD path - syncing, page not ready (poll ${attempt + 1})`);
    if (options.onSyncProgress) {
      options.onSyncProgress('syncing', attempt + 1);
    }
    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  const priceData = priceResult.data || {};
  const pricePagination = priceResult.pagination || {};
  const priceSync = priceResult.sync || {};

  // During COLD path, skip color fetch
  /** @type {{ cards?: Object[], colorOffsets?: Record<string, number> | null }} */
  let colorData = {};
  let colorPagination = {};

  if (priceSync.status === 'syncing') {
    console.log('V5: COLD path syncing (page ready), skipping color fetch');
    colorData = { cards: [] };
    colorPagination = { totalCards: pricePagination.totalCards };
  } else {
    const colorResult = await fetchFirstPage('color');

    if (!colorResult.success) {
      const errorMsg = colorResult.sync?.error || colorResult.error || 'V5 color fetch failed';
      throw new Error(errorMsg);
    }
    colorData = colorResult.data || {};
    colorPagination = colorResult.pagination || {};
    // Color offsets come alongside paginated color results
    if (colorResult.colorOffsets) {
      colorData.colorOffsets = colorResult.colorOffsets;
    }
  }

  const syncStatus = priceSync.status || 'unknown';
  console.log(
    `V5: Loaded collection [sync=${syncStatus}] - ` +
      `${priceData.cards?.length || 0} price cards, ${colorData.cards?.length || 0} color cards, ` +
      `${pricePagination.totalCards || 'unknown'} total`
  );

  const strippedPriceCards = (priceData.cards || []).map((card) => stripUnnecessaryCardData(card));
  const strippedColorCards = (colorData.cards || []).map((card) => stripUnnecessaryCardData(card));

  const simple_url = generateSimpleUrl(sourceInfo);
  const priceHasMore = pricePagination.page < pricePagination.totalPages;
  const colorHasMore = colorPagination.page
    ? colorPagination.page < colorPagination.totalPages
    : false;

  return {
    name: priceData.name || `${priceData.owner}'s ${sourceType}`,
    owner: priceData.owner || 'Unknown',
    simple_url,
    isCollection: true,
    collectionType: sourceType,
    source,
    sourceId,
    totalCards: pricePagination.totalCards,
    initialPriceCards: strippedPriceCards,
    initialColorCards: strippedColorCards,
    priceHasMore,
    colorHasMore,
    syncedAt: priceSync.syncedAt,
    syncStatus: priceSync.status,
    pageReady: priceSync.pageReady,
    colorOffsets: colorData.colorOffsets || null,
  };
}

/**
 * Generate a simple URL from source info
 * @param {{ source: string, type: string, id: string }} sourceInfo - Parsed source info
 * @returns {string|null} Simple URL like mfb.xxx or ac.123 (the short path without its slash)
 */
export function generateSimpleUrl(sourceInfo) {
  return toShortPath(sourceInfo)?.slice(1) ?? null;
}

/**
 * Loads a binder/collection/deck using the V5 API.
 * @param {string} binderUrl - Full URL or short path to the deck/binder
 * @param {Object} options - Optional parameters
 * @returns {Promise<Object>} Binder/collection result
 */
export async function loadBinder(binderUrl, options = {}) {
  const sourceInfo = extractSourceInfo(binderUrl);

  if (!sourceInfo) {
    throw new Error(`Could not parse URL: ${binderUrl}`);
  }

  // Check cache first
  const cacheKey = getCacheKey(sourceInfo.source, sourceInfo.type, sourceInfo.id);
  if (isCacheAvailable()) {
    try {
      const cached = await getCachedCollectionInitial(cacheKey);
      if (cached && !cached.isStale) {
        console.log('V5: Fresh cache hit for', cacheKey);
        return cached.data;
      }
    } catch (cacheErr) {
      console.warn('V5: Cache read failed, falling through to API:', cacheErr);
    }
  }

  const result = await loadCollectionV5(sourceInfo, options);

  // Cache the result (don't cache partial COLD path data)
  if (isCacheAvailable() && result.syncStatus !== 'syncing') {
    try {
      const etag = result.syncedAt || new Date().toISOString();
      await cacheCollectionInitial(cacheKey, result, etag);
    } catch (cacheErr) {
      console.warn('V5: Failed to cache result:', cacheErr);
    }
  }

  return result;
}
